import type { DoctorMatrixCell, DeptMetricsAllFilters, DeptMetricsAllResponse } from "@shared/api";
import { api } from "./api";
import { monthShift } from "../store";
import { RATIO_METRICS, weightedRatio } from "./ratioMetrics";

/** Sum the selected physician across ward departments before deriving ratios. */
export function doctorDetailResponse(cells: DoctorMatrixCell[], f: DeptMetricsAllFilters, doctor: string): DeptMetricsAllResponse {
  const selected = cells.filter(r => r.admdrcd === doctor && (!f.focusedDept || r.診療科 === f.focusedDept));
  const names = selected.find(r => r.医師名)?.医師名 ?? doctor;
  const index = new Map<string, Map<string, number>>();
  for (const r of selected) for (const p of r.trend) {
    for (const [date, v] of [[p.date, p.actual], [monthShift(p.date, -12), p.prevYear]] as const) {
      if (v == null) continue;
      // Overlapping current/prior request windows represent the same month, not extra activity.
      const key = `${r.診療科}|${r.集計区分}|${date}`;
      if (!index.has(key)) index.set(key, new Map([[date, v]]));
    }
  }
  const values = new Map<string, Map<string, number>>();
  for (const [key, point] of index) {
    const [dept, metric] = key.split("|");
    void dept;
    if (!values.has(metric)) values.set(metric, new Map());
    for (const [date, value] of point) values.get(metric)!.set(date, (values.get(metric)!.get(date) ?? 0) + value);
  }
  const months: string[] = [];
  for (let m = monthShift(f.asOfMonth, -(f.trendBack ?? 11)); m <= f.asOfMonth; m = monthShift(m, 1)) months.push(m);
  const ratios = ["入院単価", "在院単価", "GP単価", "DPC期間2以内率", "予定入院率", "平均在院日数"];
  const dates = new Set([...months, ...months.map(m => monthShift(m, -12)), monthShift(f.asOfMonth, -1)]);
  for (const metric of ratios) {
    const series = new Map<string, number>();
    for (const date of dates) {
      const value = weightedRatio(RATIO_METRICS[metric], m => values.get(m)?.get(date) ?? null);
      if (value != null) series.set(date, value);
    }
    values.set(metric, series);
  }
  return {
    asOfMonth: f.asOfMonth, diagnosedDept: names, trendMonths: months, sqlMs: 0,
    rows: [...values].map(([集計区分, v]) => ({
      集計区分, current: v.get(f.asOfMonth) ?? null, prevMonth: v.get(monthShift(f.asOfMonth, -1)) ?? null,
      prevYear: v.get(monthShift(f.asOfMonth, -12)) ?? null, budget: null,
      trend: months.map(date => ({ date, actual: v.get(date) ?? null, prevYear: v.get(monthShift(date, -12)) ?? null, budget: null })),
    })),
  };
}

export async function fetchDoctorDetail(f: DeptMetricsAllFilters, admdrcd: string): Promise<DeptMetricsAllResponse> {
  const requests = [];
  const from = monthShift(f.asOfMonth, -(f.trendBack ?? 11));
  for (let start = from; start <= f.asOfMonth; start = monthShift(start, 12)) {
    const end = [monthShift(start, 11), f.asOfMonth].sort()[0];
    requests.push(api.doctorMatrix({ granularity: f.granularity, iryo: f.iryo, yotei: f.yotei, fyStart: start, fyEnd: end, admdrcd }));
  }
  const result = await Promise.all(requests);
  return { ...doctorDetailResponse(result.flatMap(r => r.rows), f, admdrcd), sqlMs: result.reduce((n, r) => n + r.sqlMs, 0) };
}
